import { clsx } from 'clsx';
import { useGetList } from 'react-admin';
import { useEffect, useState } from 'react';

import { Loader } from '@components';
import {
  cropAverageCostLevelsMap,
  cropAveragePriceLevelsMap,
  CropCategory,
  CropLevels,
  cropYielLevelsMap,
} from '@types';
import { InputText } from '@components/inputs/InputText';
import { InputSelect } from '@components/inputs/InputSelect';
import { VarietiesInput } from '../VarietiesInput';
import cls from './crop-form.module.css';

interface CropCreatorFormProps {
  formValues: any;
  setFormValues: any;
  showErrors: boolean;
  selectedCategory: CropCategory | null;
  setSelectedCategory: (selectedCategory: CropCategory) => void;
}

interface CropTooltipData {
  id: CropLevels;
  info: string;
}

const cropYielInfo: CropTooltipData[] = [
  { id: CropLevels.LOW, info: cropYielLevelsMap[CropLevels.LOW] },
  { id: CropLevels.MEDIUM_LOW, info: cropYielLevelsMap[CropLevels.MEDIUM_LOW] },
  { id: CropLevels.MEDIUM, info: cropYielLevelsMap[CropLevels.MEDIUM] },
  { id: CropLevels.MEDIUM_HIGH, info: cropYielLevelsMap[CropLevels.MEDIUM_HIGH] },
  { id: CropLevels.HIGH, info: cropYielLevelsMap[CropLevels.HIGH] },
];
const cropAverageCostInfo: CropTooltipData[] = [
  { id: CropLevels.LOW, info: cropAverageCostLevelsMap[CropLevels.LOW] },
  { id: CropLevels.MEDIUM_LOW, info: cropAverageCostLevelsMap[CropLevels.MEDIUM_LOW] },
  { id: CropLevels.MEDIUM, info: cropAverageCostLevelsMap[CropLevels.MEDIUM] },
  { id: CropLevels.MEDIUM_HIGH, info: cropAverageCostLevelsMap[CropLevels.MEDIUM_HIGH] },
  { id: CropLevels.HIGH, info: cropAverageCostLevelsMap[CropLevels.HIGH] },
];
const cropAveragePriceInfo: CropTooltipData[] = [
  { id: CropLevels.LOW, info: cropAveragePriceLevelsMap[CropLevels.LOW] },
  { id: CropLevels.MEDIUM_LOW, info: cropAveragePriceLevelsMap[CropLevels.MEDIUM_LOW] },
  { id: CropLevels.MEDIUM, info: cropAveragePriceLevelsMap[CropLevels.MEDIUM] },
  { id: CropLevels.MEDIUM_HIGH, info: cropAveragePriceLevelsMap[CropLevels.MEDIUM_HIGH] },
  { id: CropLevels.HIGH, info: cropAveragePriceLevelsMap[CropLevels.HIGH] },
];
const levelOptions = [
  CropLevels.LOW,
  CropLevels.MEDIUM_LOW,
  CropLevels.MEDIUM,
  CropLevels.MEDIUM_HIGH,
  CropLevels.HIGH,
];
const typeOptions = ['Intensivo', 'Extensivo'];

const TooltipInfo = ({ info }: { info: CropTooltipData[] }) => {
  return (
    <div className={cls.info}>
      {info.map(i => (
        <div key={i.id} className={cls.infoRow}>
          <span className={cls.infoLabel}><b>{i.id}:</b> </span>
          <span className={cls.infoValue}>{i.info}</span>
        </div>
      ))}
    </div>
  );
};

export const CropForm = ({
  formValues, setFormValues, showErrors, selectedCategory, setSelectedCategory,
}: CropCreatorFormProps) => {
  const { data: cropCategories } = useGetList('crop-category');
  const showNameMxError = formValues.nameMx?.length === 0 && showErrors;
  const showCategoryError = !formValues.categoryId && showErrors;
  const categories = cropCategories?.map(c => c.nameMx) || [];
  const [loader, setLoader] = useState(!!(formValues.id && formValues.categoryId));

  const onChangeHandler = (e: any) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const onCategoryChange = (e: any) => {
    const categoryName = e.target.value;
    const category = cropCategories?.find(cc => cc.nameMx === categoryName);

    setSelectedCategory(category);
    setFormValues({
      ...formValues,
      categoryId: category?.id || null,
    });
  };

  const onSelect = (e: any, field: string) => {
    setFormValues({
      ...formValues,
      [field]: e.target.value || '',
    });
  };

  const onVarietiesChange = (varieties: string[]) => {
    setFormValues({
      ...formValues,
      varieties,
    });
  };

  useEffect(() => {
    if (cropCategories) {
      setSelectedCategory(cropCategories.find(cc => cc.id === formValues.categoryId));
    }
  }, [cropCategories]);

  useEffect(() => {
    if (selectedCategory?.nameMx) {
      setLoader(false);
    }
  }, [selectedCategory]);

  return (
    <div className={cls.form}>
      <div className={cls.row}>
        <InputText
          name="id"
          label="Id"
          labelWidth={164}
          centered
          value={formValues.id || ''}
          disabled
        />
      </div>
      <div className={cls.row}>
        <InputText
          name="nameMx"
          label="Name mx*"
          labelWidth={164}
          centered
          onChange={onChangeHandler}
          className={clsx(showNameMxError && cls.error)}
          value={formValues.nameMx || ''}
        />
      </div>
      <div className={cls.row}>
        <InputText
          name="nameEn"
          label="Name en"
          labelWidth={164}
          centered
          onChange={onChangeHandler}
          value={formValues.nameEn || ''}
        />
      </div>
      {!loader ? <div className={cls.row}>
        <InputSelect
          labelWidth={164}
          label="Category*"
          name="category"
          values={categories}
          selectedValue={selectedCategory?.nameMx || ''}
          className={clsx(showCategoryError && cls.error)}
          onChange={onCategoryChange}
          centered
        />
      </div> : <Loader />}
      <div className={cls.row}>
        <InputSelect
          labelWidth={164}
          label="Type"
          name="type"
          values={typeOptions}
          selectedValue={formValues?.type || ''}
          onChange={e => onSelect(e, 'type')}
          centered
          displayEmpty
        />
      </div>
      <div className={cls.row}>
        <InputSelect
          labelWidth={164}
          label="Yiel (Ton)"
          name="yiel"
          values={levelOptions}
          selectedValue={formValues?.yiel || ''}
          onChange={e => onSelect(e, 'yiel')}
          tooltip={<TooltipInfo info={cropYielInfo}/>}
          centered
          displayEmpty
        />
      </div>
      <div className={cls.row}>
        <InputSelect
          labelWidth={164}
          label="Average Cost Per Ha"
          name="averageCost"
          values={levelOptions}
          selectedValue={formValues?.averageCost || ''}
          onChange={e => onSelect(e, 'averageCost')}
          tooltip={<TooltipInfo info={cropAverageCostInfo}/>}
          centered
          displayEmpty
        />
      </div>
      <div className={cls.row}>
        <InputSelect
          labelWidth={164}
          label="Average Price"
          name="averagePrice"
          values={levelOptions}
          selectedValue={formValues?.averagePrice || ''}
          onChange={e => onSelect(e, 'averagePrice')}
          tooltip={<TooltipInfo info={cropAveragePriceInfo} />}
          centered
          displayEmpty
        />
      </div>
      <div className={cls.row}>
        <VarietiesInput
          varieties={formValues?.varieties || []}
          onChange={onVarietiesChange}
          labelWidth={164}
        />
      </div>
    </div>
  );
};
